// 運営ツール: テナント作成の入力規則と控え（規則はサーバー側 services/admin_ops.py のテナント作成と同期させること）
import { domainListErrors, hasDomainErrors, joinDomains } from "./opsDomain";

// t_user.username / m_company.name の列長（サーバー constants.USER_NAME_MAX_LENGTH）
export const NAME_MAX_LENGTH = 64;
// サーバー constants.MAIL_ADDRESS_MAX_LENGTH
export const EMAIL_MAX_LENGTH = 64;
// ユーザー管理のアカウント追加と同じ（サーバー constants.USER_PASSWORD_MIN_LENGTH / MAX_LENGTH）
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 32;
// 運営用アカウントのパスワードの長さ（画面で生成して完了画面に出す）
export const OPERATOR_PASSWORD_LENGTH = 12;

const EMAIL_PATTERN = /^[^@\s]+@[^@\s.]+(?:\.[^@\s.]+)+$/;
// 運営用メールは管理者のメールに「+名前」を付けて作る。その名前の部分
const OPERATOR_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9._-]{0,30}[a-z0-9])?$/;
// 会社名は utf8mb3 の列に入るため、U+FFFF より後ろ（絵文字等）は保存できない
const FOUR_BYTE_PATTERN = /[\u{10000}-\u{10FFFF}]/u;

export const isValidEmail = (email) => typeof email === "string" && email.length <= EMAIL_MAX_LENGTH && EMAIL_PATTERN.test(email);

/** 運営用メール＝管理者のメールに「+名前」を付けたもの（例: yayoi@prizmo.ai → yayoi+ymizuno@prizmo.ai）。作れなければ空文字 */
export const operatorEmailOf = (adminEmail, operatorName) => {
    if (!isValidEmail(adminEmail) || !operatorName) return "";
    const at = adminEmail.lastIndexOf("@");
    return `${adminEmail.slice(0, at)}+${operatorName}${adminEmail.slice(at)}`;
};

export const companyNameError = (name) => {
    if (!(name ?? "").trim()) return "会社名を入力してください";
    if (name.length > NAME_MAX_LENGTH) return `${NAME_MAX_LENGTH}文字以内で入力してください`;
    if (FOUR_BYTE_PATTERN.test(name)) return "絵文字など一部の文字は使えません";
    return "";
};

export const operatorNameError = (operatorName) => {
    if (!operatorName) return "名前を入力してください";
    return OPERATOR_NAME_PATTERN.test(operatorName) ? "" : "半角の英小文字・数字・. _ -（先頭と末尾は英数字）、32文字以内で入力してください";
};

const emailError = (email) => {
    if (!email) return "メールアドレスを入力してください";
    return isValidEmail(email) ? "" : "メールアドレスの形式が正しくありません";
};

const passwordError = (password) => {
    const length = (password ?? "").length;
    return length >= PASSWORD_MIN_LENGTH && length <= PASSWORD_MAX_LENGTH ? "" : `${PASSWORD_MIN_LENGTH}文字以上${PASSWORD_MAX_LENGTH}文字以下で入力してください`;
};

const operatorError = (operatorName, adminEmail) => {
    const error = operatorNameError(operatorName);
    if (error) return error;
    const email = operatorEmailOf(adminEmail, operatorName);
    if (email.length > EMAIL_MAX_LENGTH) return `メールアドレスが${EMAIL_MAX_LENGTH}文字を超えます（現在 ${email.length} 文字）`;
    return "";
};

/** 項目ごとのエラー（無ければ空文字）。domains は許可ドメインの入力行 */
export const tenantFormErrors = (form) => {
    const domainErrors = domainListErrors(form.domains);
    return {
        companyName: companyNameError(form.companyName),
        domains: hasDomainErrors(form.domains) ? (domainErrors.list || "入力内容を確認してください") : "",
        adminEmail: emailError(form.adminEmail),
        adminPassword: passwordError(form.adminPassword),
        operatorName: operatorError(form.operatorName, form.adminEmail),
    };
};

export const hasFormErrors = (errors) => Object.values(errors).some(Boolean);

/** 送信する値 */
export const tenantCreateParams = (form, operatorPassword) => ({
    company_name: form.companyName,
    domain: joinDomains(form.domains),
    // 管理者アカウントの名前は会社名にする
    admin_name: form.companyName,
    admin_email: form.adminEmail,
    admin_password: form.adminPassword,
    operator_email: operatorEmailOf(form.adminEmail, form.operatorName),
    operator_password: operatorPassword,
});

export const CUSTOMER_MEMO_TITLE = "お客様に渡す控え（管理者アカウント）";
export const OPERATOR_MEMO_TITLE = "運営用アカウントの控え";

/** 顧客に渡す控え（運営用アカウントの情報は載せない） */
export const buildCustomerMemo = (result, adminPassword, loginUrl) => [
    `会社名: ${result.tenant.company_name}`,
    `アカウント名: ${result.admin_account.name}`,
    `メールアドレス: ${result.admin_account.email}`,
    `初期パスワード: ${adminPassword}`,
    `許可ドメイン: ${result.tenant.domains.join(", ")}`,
    `ログインURL: ${loginUrl}`,
].join("\n");

/** 運営用アカウントの控え */
export const buildOperatorMemo = (result, operatorPassword, loginUrl) => [
    `会社名: ${result.tenant.company_name}（テナントID: ${result.tenant.t_user_id}）`,
    `運営用アカウント: ${result.operator_account.email}`,
    `パスワード: ${operatorPassword}`,
].join("\n");

/** まとめてコピー用（顧客用・運営用を見出し付きで続けて並べる） */
export const buildAllMemo = (result, adminPassword, operatorPassword, loginUrl) => [
    `【${CUSTOMER_MEMO_TITLE}】`,
    buildCustomerMemo(result, adminPassword, loginUrl),
    "",
    `【${OPERATOR_MEMO_TITLE}】`,
    buildOperatorMemo(result, operatorPassword, loginUrl),
].join("\n");
